/** Shared app shell: top bar + sidebar nav + routed content area. */
import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

interface NavItem {
  to: string;
  label: string;
  disabled?: boolean;
  hint?: string;
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard" },
  { to: "/users", label: "Users" },
  { to: "/orgs", label: "Organizations" },
  { to: "/invitations", label: "Invitations" },
  { to: "/access-requests", label: "Access Requests" },
  { to: "/catalog", label: "Catalog" },
  { to: "/audit", label: "Audit" },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    "block rounded px-3 py-2 text-sm font-medium",
    isActive ? "bg-oxbo text-white" : "text-slate-700 hover:bg-slate-100",
  ].join(" ");
}

export function Layout(): ReactNode {
  const { identity, logout } = useAuth();

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-oxbo">OXBO</span>
          <span className="text-sm text-slate-500">Admin</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-600">
          {identity && <span title={identity.userId}>{identity.email || identity.userId}</span>}
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <nav className="w-56 shrink-0 border-r border-slate-200 bg-white p-3">
          <ul className="space-y-1">
            {NAV.map((item) =>
              item.disabled ? (
                <li key={item.to}>
                  <span
                    className="flex items-center justify-between rounded px-3 py-2 text-sm text-slate-400"
                    title={item.hint}
                  >
                    {item.label}
                    {item.hint && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">
                        {item.hint}
                      </span>
                    )}
                  </span>
                </li>
              ) : (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.to === "/"} className={navClass}>
                    {item.label}
                  </NavLink>
                </li>
              ),
            )}
          </ul>
        </nav>

        <main className="min-h-0 flex-1 overflow-auto bg-slate-50 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
